import { FC, useCallback, useMemo, useState } from 'react';
import {
	useRecoilState,
	useRecoilValue,
	useResetRecoilState,
	useSetRecoilState,
} from 'recoil';
import { Button, Loader } from '@storybook';

import { ArchiveConfirmationModal } from 'views/sessions';
import { useNewRoles } from 'views/user-roles/hooks';
import {
	RoleAndPermissionNavigation,
	UserRoleNavigation,
} from 'views/user-roles/constant';
import {
	AddNewRoleFormState,
	PermissionNavigation,
	SelectedUserRoleState,
	UserRolePermissionNavigation,
} from 'views/user-roles/store';
import { AgentSelectedService } from '../agent-selected-service';
import { AllowPermission } from '../allow-permission';
import { AssinedUser } from '../assined-user';
import { DropInfoOptions } from '../info-drop-options';
import { AddNewRoleModal } from '../add-role-modal';

import './complaince-agent.scss';

interface IComplainceAgent {
	isUserPermissionWrite?: boolean;
}

export const ComplainceAgent: FC<IComplainceAgent> = ({
	isUserPermissionWrite = true,
}) => {
	const setUserRoleNavigate = useSetRecoilState(UserRolePermissionNavigation);
	const setNavigate = useSetRecoilState(PermissionNavigation);
	const resetAddRoleForm = useResetRecoilState(AddNewRoleFormState);
	const navigate = useRecoilValue(PermissionNavigation);

	const [isAddRoleModalOpen, setIsAddRoleModalOpen] = useState<boolean>(false);
	const [selectedUserRole, setSelectedUserRole] = useRecoilState(
		SelectedUserRoleState
	);

	const handleCloseModal = useCallback(() => {
		setIsAddRoleModalOpen(prev => !prev);
	}, []);

	const {
		handleDeleteUserRole,
		isOpenDeleteModal,
		handleOpenDelete,
		addNewRoleLoader,
		handleUpdateRole,
		updateRoleLoader,
		isEditRoleDisabled,
	} = useNewRoles({ handleCloseModel: handleCloseModal });

	const handleBackToPermission = () => {
		setUserRoleNavigate(UserRoleNavigation.AddedRoleCard);
		setSelectedUserRole(null);
		resetAddRoleForm();
	};

	const onEditInviteRole = () => {
		handleCloseModal();
		setNavigate(RoleAndPermissionNavigation.EditRole);
	};

	const disableNextBtn = useMemo(() => {
		switch (navigate) {
			case RoleAndPermissionNavigation.AddNewRole:
			case RoleAndPermissionNavigation.EditRole:
				return isEditRoleDisabled;
			default:
				return false;
		}
	}, [isEditRoleDisabled, navigate]);

	return (
		<div className="ComplainceAgent--container">
			<div
				className="ComplainceAgent--container__back"
				onClick={handleBackToPermission}
			>
				<div className="ComplainceAgent--container__back--icon">
					<i className="ri-arrow-left-s-line" />
				</div>
				<div className="ComplainceAgent--container__back--label">
					Back to Roles and permission
				</div>
			</div>

			<div className="ComplainceAgent--container__userAccess">
				<div className="agent--container">
					<div className="agent--container__header">
						<div className="agent--container__header--title">
							<div className="agent--container__header--title__heading">
								<div className="agent--container__header--title__heading--text">
									<div className="agent--container__header--title__heading--text__label">
										{selectedUserRole?.name ?? 'NA'}
									</div>
									{isUserPermissionWrite && (
										<div className="agent--container__header--title__heading--text__btn">
											<div
												className="agent--container__header--title__heading--text__btn--label"
												onClick={onEditInviteRole}
											>
												Edit
											</div>
											<DropInfoOptions
												handleDelete={e =>
													handleOpenDelete(e, selectedUserRole)
												}
												showEdit={false}
												isDisabled
												isDeleteDisabled={true}
											/>
										</div>
									)}
								</div>
							</div>
							<div className="agent--container__header--title__subHeader">
								{selectedUserRole?.description ?? 'NA'}
							</div>
						</div>
					</div>
					<div className="agent--container__services">
						<AgentSelectedService />
					</div>
					<div className="agent--container__permission">
						<div className="agent--container__permission--table">
							<AllowPermission isDisable />
						</div>
					</div>
				</div>
				<div className="assinedUser--container">
					<AssinedUser isUserPermissionWrite={isUserPermissionWrite} />
				</div>
			</div>

			{isOpenDeleteModal && (
				<ArchiveConfirmationModal
					description={`Deleting ${selectedUserRole?.name}  role will remove user’s permissions from the
					system.`}
					boldDescription={`" Do you still want to delete ${selectedUserRole?.name} role?"`}
					label="Delete"
					title="Are you sure?"
					visible={isOpenDeleteModal}
					showLoader={addNewRoleLoader}
					handleModal={handleDeleteUserRole}
				/>
			)}

			{isAddRoleModalOpen && (
				<AddNewRoleModal
					isAddRoleModalOpen={isAddRoleModalOpen}
					handleCloseModel={handleCloseModal}
				>
					<div className="AddNewRoleModal--container__footer">
						<Button
							label="Cancel"
							type="button__filled--secondary"
							handleClick={handleCloseModal}
						/>
						<Button
							label={
								updateRoleLoader ? (
									<Loader
										type="loader"
										dimension={22}
										className="loader-white"
									/>
								) : (
									'Save'
								)
							}
							type="button__filled--primary"
							handleClick={handleUpdateRole}
							disabled={disableNextBtn || updateRoleLoader}
						/>
					</div>
				</AddNewRoleModal>
			)}
		</div>
	);
};
